import React from 'react';
import { observer, PropTypes as MobxPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';

// import { CheckoutStore } from '../../stores/CheckoutStore';
import { InstallationStore } from '../../stores/InstallationStore';
import { NumberHelper } from '../../helpers/NumberHelper';
import { InstallationData } from '../../models/InstallationData';
import { SecondaryButton } from '../secondary-button';
import { SimpleButton } from '../simple-button';
import { Spinner } from '../spinner';
import { InstallationLine } from './installation-line';
import { DatePickerInput } from './date-picker-input';
import styles from './InstallationForm.css';

const IPropTypes = {
  addressId: PropTypes.string.isRequired,
  cart: MobxPropTypes.arrayOrObservableArrayOf(PropTypes.shape({
    serviceAmount: PropTypes.number.isRequired,
    product: PropTypes.shape({
      id: PropTypes.string.isRequired,
      serviceProduct: PropTypes.object,
    }).isRequired,
  })).isRequired,
  className: PropTypes.string,
  onInvalidAddress: PropTypes.func,
  onSubmit: PropTypes.func,
  startDate: PropTypes.instanceOf(Date).isRequired,
};

const defaultProps = {
  className: '',
  onInvalidAddress: () => {},
  onSubmit: () => {},
};

class InstallationForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      date: this.props.startDate ? new Date(this.props.startDate) : null,
      allOptionsSelected: false,
    };

    this.selections = [];
    this.rejections = [];

    this._clearDate = this._clearDate.bind(this);
    this._setDate = this._setDate.bind(this);
    this._updateDate = this._updateDate.bind(this);
    this._rejectionCallback = this._rejectionCallback.bind(this);
    this._selectCallback = this._selectCallback.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onCoordinationCancellation = this._onCoordinationCancellation.bind(this);
  }

  componentDidMount() {
    InstallationStore.clearInstallations();
  }

  _updateDate(date) {
    const newDate = new Date(date.valueOf());
    newDate.setHours(0);
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    this.setState({
      date: newDate,
    });
  }

  _setDate(event) {
    event.preventDefault();
    // this._fetchOptions();
    this._fetchAllOptions();
  }

  _clearDate() {
    this.setState({
      date: this.props.startDate ? new Date(this.props.startDate) : null,
    });
    InstallationStore.clearInstallations();
  }

  async _fetchOptions() {
    // CheckoutStore.cartHasInstallations
    await InstallationStore.fetchInstallationOptions(
      // Amount of installations overall.
      this.props.cart.reduce((acc, item) => acc + item.serviceAmount, 0),
      this.props.addressId,
      this.state.date,
    );
    if (InstallationStore.invalidAddressError) {
      this.props.onInvalidAddress(InstallationStore.invalidAddressError);
    }
  }

  async _fetchAllOptions() {
    const products = this.props.cart.reduce((acc, item) => {
      if (item.serviceAmount > 0) {
        acc[item.product.id] = acc[item.product.id] == null ? 1 : acc[item.product.id] + 1;
      }
      return acc;
    }, {});
    await InstallationStore.fetchAllInstallationOptions(
      products,
      this.props.addressId,
      this.state.date,
    );
    if (InstallationStore.invalidAddressError) {
      this.props.onInvalidAddress(InstallationStore.invalidAddressError);
    }
  }

  _clearElementFromLists(productId, index) {
    this.selections = this.selections.filter(item => (
      item.index !== index ||
      item.productId !== productId
    ));
    this.rejections = this.rejections.filter(item => (
      item.index !== index ||
      item.productId !== productId
    ));
  }

  _checkLists() {
    const productsWithInstallation = this.props.cart.map(item => (
      item.product.serviceProduct && !item.product.serviceProduct.isWarranty &&
       item.serviceAmount ? item.serviceAmount : 0
    )).reduce((acc, serviceAmount) => acc + serviceAmount, 0);
    this.setState({
      allOptionsSelected: (
        this.selections.length + this.rejections.length === productsWithInstallation
      ),
    });
  }

  _selectCallback(productId, index) {
    return (option) => {
      this._clearElementFromLists(productId, index);
      this.selections.push({
        productId,
        index,
        option,
      });
      this._checkLists();
    };
  }

  _rejectionCallback(productId, index) {
    return () => {
      this._clearElementFromLists(productId, index);
      this.rejections.push({
        productId,
        index,
      });
      this._checkLists();
    };
  }

  _installationPrice() {
    const individualInstallations = [];
    // We need to know how many different options have been chosen,
    // regardless of the products being installed.
    this.selections.forEach((item) => {
      let info = individualInstallations.find(data => data.id === item.option.id);
      if (!info) {
        info = {
          id: item.option.id,
          price: item.option.extraCost,
        };
        individualInstallations.push(info);
      }
    });
    return individualInstallations.reduce((acc, item) => acc + item.price, 0);
  }

  _onSubmit(event) {
    event.preventDefault();
    if (!this.state.allOptionsSelected) {
      return;
    }

    const finalInstallations = this.props.cart.map((item) => {
      if (!item.serviceAmount) {
        return null;
      }
      const selectedInstallations = [];
      this.selections.forEach((entry) => {
        if (entry.productId !== item.product.id) {
          return;
        }
        let info = selectedInstallations.find(data => data.id === entry.option.id);
        if (!info) {
          info = {
            id: entry.option.id,
            amount: 0,
            date: entry.option.date.toISO(),
            startHour: entry.option.startHour,
            endHour: entry.option.endHour,
            extraCost: entry.option.extraCost,
          };
          selectedInstallations.push(info);
        }
        info.amount += 1;
      });
      return new InstallationData({
        productId: item.product.id,
        selectedOptions: selectedInstallations,
      });
    }).filter(Boolean);
    this.props.onSubmit(finalInstallations);
  }

  _onCoordinationCancellation(event) {
    event.preventDefault();
    // Submit no installations
    this.props.onSubmit([]);
  }

  render() {
    // Clone the date that comes from props.
    const datePickerFromDate = new Date(this.props.startDate);
    // The date selected can't be the same day as the day the shipping is set.
    // datePickerFromDate.setDate(datePickerFromDate.getDate() + 1);
    // Set hours, minutes, seconds and milliseconds to zero. Essentially the start of the day.
    datePickerFromDate.setHours(0);
    datePickerFromDate.setMinutes(0);
    datePickerFromDate.setSeconds(0);
    datePickerFromDate.setMilliseconds(0);
    const datePickerToDate = new Date(datePickerFromDate.getTime());
    datePickerToDate.setFullYear(datePickerToDate.getFullYear() + 1);

    return (
      <div className={[styles.container, this.props.className].join(' ')}>
        {
          !InstallationStore.installationOptions &&
          !InstallationStore.loading &&
          <form onSubmit={this._setDate}>
            <p className={styles.formText}>
              Ingresá la fecha a partir de la cual desea
              &nbsp;recibir la instalación.
            </p>
            <DatePickerInput
              fromDate={datePickerFromDate}
              toDate={datePickerToDate}
              onChange={this._updateDate}
              value={this.state.date}
            />
            {
              !!InstallationStore.noInstallationsError &&
              <p className={styles.errorMessage}>
                {InstallationStore.noInstallationsError.message}
              </p>
            }
            {
              !!InstallationStore.error &&
              <p className={styles.errorMessage}>
                Ha habido un error, intente nuevamente por favor.
              </p>
            }
            <SecondaryButton
              className={styles.button}
              disabled={!this.state.date}
            >
              Buscar opciones
            </SecondaryButton>
            <SimpleButton
              className={styles.button}
              onClick={this._onCoordinationCancellation}
            >
              Coordinar luego
            </SimpleButton>
          </form>
        }
        {
          InstallationStore.loading &&
          <Spinner className={styles.spinner} />
        }
        {
          !!InstallationStore.installationOptions &&
          <div>
            <SecondaryButton
              className={styles.button}
              onClick={this._clearDate}
            >
              Elegir otra fecha
            </SecondaryButton>
            {
              this.props.cart.map((item) => {
                if (!item.product.serviceProduct
                  || !item.serviceAmount || item.product.serviceProduct.isWarranty) {
                  return null;
                }
                const lines = [];
                for (let i = 0; i < item.serviceAmount; i += 1) {
                  lines.push(<InstallationLine
                    key={i}
                    options={InstallationStore.installationOptions[item.product.id]}
                    onSelect={this._selectCallback(item.product.id, i)}
                    onReject={this._rejectionCallback(item.product.id, i)}
                    product={item.product}
                  />);
                }
                return lines;
              }).filter(Boolean)
            }
            {
              !!this._installationPrice() &&
              <p className={styles.warningText}>
                Deberá pagar un extra de ${NumberHelper.localizeNumber(this._installationPrice())}
                &nbsp;por viáticos (se cobra al momento de la instalación).
              </p>
            }
            <SecondaryButton
              className={styles.continueButton}
              disabled={!this.state.allOptionsSelected}
              onClick={this._onSubmit}
            >
              Continuar
            </SecondaryButton>
          </div>
        }
      </div>
    );
  }
}

InstallationForm.propTypes = IPropTypes;
InstallationForm.defaultProps = defaultProps;

const ObservedInstallationForm = observer(InstallationForm);

export { ObservedInstallationForm as InstallationForm };



// WEBPACK FOOTER //
// ./src/common/installation-form/InstallationForm.jsx